/**
 * DataPreview.jsx — the Results surface: header, KPI, tab bar, tables.
 *
 * Phase 5 (screen redesign): the ordinary result tables (Total Holdings,
 * Allocation, per-ETF holdings) run on the shared `DataTable` — no private
 * table variant lives here anymore, the 8px pointer-only resize handle is gone
 * and the company name wraps instead of depending on a native `title`.
 * Data, sort, the 100-row cap, the allocation editing/recalculation and every
 * business rule are unchanged.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, Save, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BreakdownTab } from "./BreakdownTab";
import { DiversificationTab } from "./diversification/DiversificationTab";
import { allocationKeyFor } from "@/utils/allocation";
import { errorText } from "@/utils/errorText";
import { formatNumber, formatPercent, formatPercentPoints } from "@/utils/format";
import { StatsBar } from "@/components/StatsBar";
import { FilterSelect } from "@/components/shared/FilterSelect";
import {
  ConfirmDialog, DataTable, EmptyState, ErrorState, NotCalculable,
  Tabs, Toolbar,
} from "@/components/system";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** A weight is a neutral quantity: no semantic colour, only emphasis. */
const weightClass = (weight) => (weight >= 0.03 ? "text-content font-semibold" : "text-content-secondary");

const formatWeight = (weight) => {
  if (weight === 0 || weight === undefined || weight === null) return "\u2014";
  return formatPercent(weight);
};

const dash = (v) => v || "\u2014";

/* ================ TOTAL HOLDINGS ================ */

function TotalHoldingsTab({ data, etfNames }) {
  const rows = useMemo(() => (data || []), [data]);

  const columns = useMemo(() => [
    {
      key: "company", header: "Società", sticky: true, minWidth: 220,
      className: "!text-content font-medium",
      render: (row) => (
        // A3.11 / v2.2 — the full name wraps: nothing important hides in a title.
        // Below 1024px the descriptive columns are folded in here (A3.11) so the
        // comparison columns (% totale + per-ETF) stay side by side.
        <span className="block max-w-[170px] sm:max-w-[260px] leading-tight">
          <span className="block break-words">{row.company}</span>
          <span className="block lg:hidden break-words text-label text-content-muted font-normal">
            {[row.ticker, dash(row.sector), dash(row.country)].filter(Boolean).join(" · ")}
          </span>
        </span>
      ),
    },
    {
      key: "ticker", header: "Ticker", minWidth: 90,
      className: "font-mono hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "country", header: "Paese", minWidth: 110, render: (r) => dash(r.country),
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "area", header: "Area", minWidth: 100, render: (r) => dash(r.area),
      className: "hidden xl:table-cell", headerClassName: "hidden xl:table-cell",
    },
    {
      key: "currency", header: "Valuta", minWidth: 90, className: "font-mono hidden xl:table-cell",
      headerClassName: "hidden xl:table-cell", render: (r) => dash(r.currency),
    },
    {
      key: "currency_area", header: "Area valuta", minWidth: 120, render: (r) => dash(r.currency_area),
      className: "hidden xl:table-cell", headerClassName: "hidden xl:table-cell",
    },
    {
      key: "sector", header: "Settore", minWidth: 140, render: (r) => dash(r.sector),
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "pct_total", header: "% totale", numeric: true, minWidth: 110,
      headerClassName: "bg-table-selected",
      className: "bg-table-selected",
      render: (row) => (
        <span className={weightClass(row.pct_total)}>{formatWeight(row.pct_total)}</span>
      ),
    },
    ...etfNames.map((etf) => ({
      key: `etf:${etf}`, header: etf, numeric: true, minWidth: 110,
      accessor: (row) => row.etf_weights?.[etf] ?? 0,
      render: (row) => (
        <span className={weightClass(row.etf_weights?.[etf] || 0)}>
          {formatWeight(row.etf_weights?.[etf] || 0)}
        </span>
      ),
    })),
  ], [etfNames]);

  return (
    <DataTable
      testId="total-holdings-table"
      columns={columns}
      rows={rows}
      limit={100}
      countLabel="holdings"
      initialSort={{ field: "pct_total", dir: -1 }}
      density="compact"
      maxHeight="58vh"
      caption="Esposizione totale ponderata per società"
      getRowId={(row, i) => `${row.ticker || row.company}-${i}`}
      emptyTitle="Nessuna holding da mostrare"
      emptyDescription="L'analisi non ha prodotto holdings per questo portafoglio."
      footer={rows.length > 100 ? (
        <p className="text-label text-content-muted">
          Vista limitata alle prime 100 holdings di {formatNumber(rows.length)} — l'export Excel contiene tutto.
        </p>
      ) : null}
    />
  );
}

/* ================ ALLOCATION ================ */

function AllocationTab({ data, sessionId, onRecalculated }) {
  const [allocations, setAllocations] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    const init = {};
    data.forEach((row) => {
      init[allocationKeyFor(row)] = row.allocation != null ? row.allocation * 100 : 0;
    });
    setAllocations(init);
    setDirty(false);
  }, [data]);

  const handleChange = (allocationKey, value) => {
    setAllocations((prev) => ({ ...prev, [allocationKey]: value }));
    setDirty(true);
  };

  const totalPct = Object.values(allocations).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const isTotalValid = Math.abs(totalPct - 100) < 0.01;

  const requestSave = () => {
    if (!isTotalValid) { setConfirmOpen(true); return; }
    runSave();
  };

  const runSave = async () => {
    const allocMap = {};
    data.forEach((row) => {
      const key = allocationKeyFor(row);
      allocMap[key] = (parseFloat(allocations[key]) || 0) / 100;
    });
    setSaving(true);
    setSaveError(null);
    try {
      const { data: result } = await axios.post(`${API}/recalculate`, {
        session_id: sessionId,
        allocations: allocMap,
      });
      onRecalculated(result);
      setDirty(false);
      setSavedAt(new Date());
      toast.success("Allocazioni salvate e dati ricalcolati");
    } catch (err) {
      setSaveError(errorText(err, "Ricalcolo non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  const handleEqualise = () => {
    if (!data || data.length === 0) return;
    const equal = parseFloat((100 / data.length).toFixed(4));
    const init = {};
    data.forEach((row) => { init[allocationKeyFor(row)] = equal; });
    setAllocations(init);
    setDirty(true);
  };

  const columns = useMemo(() => [
    {
      key: "etf_name", header: "Nome ETF", sticky: true,
      // Phase 6 — narrower identity column below 1024px so the allocation input
      // is editable without scrolling the table sideways at 390px.
      className: "!text-content font-medium min-w-[150px] lg:min-w-[220px]",
      headerClassName: "min-w-[150px] lg:min-w-[220px]",
      render: (row) => (
        <span className="block max-w-[150px] lg:max-w-[280px] leading-tight">
          <span className="block break-words">{row.etf_name}</span>
          {/* Phase 6 — below 1024px only name + allocation stay as columns, so
              the input is editable without scrolling the table sideways. */}
          <span className="block lg:hidden break-words text-label text-content-muted font-normal">
            {[row.ticker, dash(row.isin)].filter(Boolean).join(" · ")}
          </span>
        </span>
      ),
    },
    {
      key: "ticker", header: "Ticker", minWidth: 100,
      className: "font-mono !text-brand-accent font-semibold hidden lg:table-cell",
      headerClassName: "hidden lg:table-cell",
    },
    {
      key: "isin", header: "ISIN", minWidth: 140, render: (r) => dash(r.isin),
      className: "font-mono hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "aum_mio", header: "AUM (Mio)", numeric: true, minWidth: 120,
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
      render: (row) => (row.aum_mio != null
        ? formatNumber(row.aum_mio, { decimals: 1 })
        : <NotCalculable compact reason="AUM non pubblicato per questo ETF" />),
    },
    {
      key: "ter_pct", header: "TER (%)", numeric: true, minWidth: 110,
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
      render: (row) => (row.ter_pct != null
        ? formatPercentPoints(row.ter_pct)
        : <NotCalculable compact reason="TER non pubblicato per questo ETF" />),
    },
    {
      key: "allocation", header: "Allocazione (%)", numeric: true, minWidth: 110, sortable: false,
      headerClassName: "bg-table-selected",
      className: "bg-table-selected",
      render: (row) => {
        const key = allocationKeyFor(row);
        return (
          <input
            data-testid={`alloc-input-${key}`}
            type="number" step="0.01" min="0" max="100"
            value={allocations[key] ?? ""}
            onChange={(e) => handleChange(key, e.target.value)}
            aria-label={`Allocazione di ${row.etf_name || row.ticker || row.isin} in percentuale`}
            className="w-full h-10 text-right bg-transparent text-content font-semibold tabular
                       rounded px-1 focus:outline-none focus-visible:bg-surface-subtle/50
                       focus-visible:ring-2 focus-visible:ring-focus/40
                       [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        );
      },
    },
  ], [allocations]);

  return (
    <div className="space-y-3">
      <Toolbar testId="allocation-toolbar" className="justify-between">
          <div className="min-w-0">
            <p className="text-sm text-content">Pesi del portafoglio analizzato</p>
            <p
              data-testid={dirty ? "save-allocation-dirty" : "save-allocation-hint"}
              className="text-xs text-content-muted mt-0.5"
            >
              {dirty
                ? "Modifiche non salvate: salva per ricalcolare i risultati."
                : savedAt
                ? `Allocazioni salvate alle ${savedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}. Modifica un peso per salvare di nuovo.`
                : "Nessuna modifica da salvare: cambia un peso per abilitare il salvataggio."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="allocation-total"
              role="status"
              className={`inline-flex items-center min-h-10 px-3 rounded-full border text-sm tabular
                          ${isTotalValid
                            ? "border-line text-content-secondary"
                            : "border-warning/40 text-warning bg-warning-solid/10"}`}
            >
              Totale: {formatPercentPoints(totalPct)}
            </span>
            <Button
              variant="ghost" size="sm" onClick={handleEqualise}
              className="h-10 px-3 text-content-secondary"
            >
              Pesi uguali
            </Button>
            <Button
              data-testid="save-allocation-btn"
              size="sm" onClick={requestSave} disabled={!dirty || saving}
              className="h-10 px-4 bg-brand hover:bg-brand-hover text-content-on-brand"
            >
              {saving
                ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Salvataggio…</>
                : <><Save className="w-3.5 h-3.5 mr-1.5" />Salva e ricalcola</>}
            </Button>
          </div>
      </Toolbar>

      {!isTotalValid && (
        <p data-testid="allocation-total-warning" role="status" className="text-xs text-warning">
          La somma delle allocazioni è {formatPercentPoints(totalPct)} invece di 100 %.
          Puoi salvare comunque: i pesi verranno usati così come sono.
        </p>
      )}

      {saveError && (
        <ErrorState
          testId="allocation-error"
          className="!py-6"
          title="Ricalcolo non riuscito"
          description={saveError}
          onRetry={runSave}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        testId="allocation-confirm"
        title="Salvare con un totale diverso da 100 %?"
        description={`La somma delle allocazioni è ${formatPercentPoints(totalPct)}.`}
        consequence="I risultati verranno ricalcolati con questi pesi così come sono: le percentuali mostrate non si riferiranno a un portafoglio completo."
        confirmLabel="Salva e ricalcola"
        onConfirm={runSave}
      />

      <DataTable
        testId="allocation-table"
        columns={columns}
        rows={data || []}
        countLabel="ETF"
        density="compact"
        maxHeight="38vh"
        caption="Allocazione degli ETF del portafoglio"
        getRowId={(row, i) => allocationKeyFor(row) || i}
        emptyTitle="Nessun ETF da allocare"
      />
    </div>
  );
}

/* ================ SINGLE ETF ================ */

function EtfTab({ etf, holdings }) {
  const columns = useMemo(() => [
    {
      key: "Company", header: "Società", sticky: true, minWidth: 220,
      className: "!text-content font-medium",
      render: (row) => (
        <span className="block max-w-[170px] sm:max-w-[280px] leading-tight">
          <span className="block break-words">{row.Company}</span>
          {/* A3.11 — below 1024px country/currency/sector fold in here, Società +
              Ticker + Peso stay the comparison columns (same model as Holdings
              totali: no separate mobile layout). */}
          <span className="block lg:hidden break-words text-label text-content-muted font-normal">
            {[dash(row.Sector), dash(row.Country)].filter(Boolean).join(" · ")}
          </span>
        </span>
      ),
    },
    { key: "Ticker", header: "Ticker", minWidth: 100, className: "font-mono" },
    {
      key: "Country", header: "Paese", minWidth: 110, render: (r) => dash(r.Country),
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "Currency", header: "Valuta", minWidth: 90, render: (r) => dash(r.Currency),
      className: "font-mono hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "Sector", header: "Settore", minWidth: 140, render: (r) => dash(r.Sector),
      className: "hidden lg:table-cell", headerClassName: "hidden lg:table-cell",
    },
    {
      key: "Weight", header: "Peso", numeric: true, minWidth: 110,
      render: (row) => <span className={weightClass(row.Weight)}>{formatWeight(row.Weight)}</span>,
    },
  ], []);

  return (
    <DataTable
      testId={`etf-table-${etf}`}
      columns={columns}
      rows={holdings || []}
      countLabel="holdings"
      initialSort={{ field: "Weight", dir: -1 }}
      density="compact"
      maxHeight="58vh"
      caption={`Holdings dell'ETF ${etf}`}
      getRowId={(row, i) => `${row.Ticker || row.Company}-${i}`}
      emptyTitle="Nessuna holding disponibile per questo ETF"
    />
  );
}

/* ================ MAIN EXPORT ================ */

export function DataPreview({
  processedData, setProcessedData,
  activeTab, setActiveTab,
  etfNames, sessionId,
}) {
  if (!processedData) {
    return (
      <div className="preview-panel h-full">
        <EmptyState
          testId="results-empty"
          icon={AlertCircle}
          title="Nessun risultato ancora"
          description="Seleziona gli ETF nel workspace, imposta i pesi e avvia l'analisi: i risultati compariranno qui."
        />
      </div>
    );
  }

  const handleRecalculated = (result) => {
    setProcessedData((prev) => ({
      ...prev,
      total_holdings: result.total_holdings,
      allocation: result.allocation,
    }));
  };

  const portfolioViews = [
    { id: "total-holdings", label: "Holdings totali" },
    { id: "allocation", label: "Allocazione" },
    { id: "breakdown", label: "Ripartizione" },
    { id: "diversification", label: "Diversificazione" },
  ];
  const isEtfView = !portfolioViews.some((v) => v.id === activeTab);

  return (
    <div className="preview-panel h-full" data-testid="results-view">
      {/* Follow-up: title and KPI share one band — the data starts higher. */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-4">
        <header data-testid="results-header" className="min-w-0">
          <p className="text-label uppercase tracking-wide text-content-muted">Analisi completata</p>
          <h2 className="text-lg md:text-xl font-semibold tracking-tight text-content mt-0.5">
            Risultati del portafoglio
          </h2>
          <p className="text-sm text-content-muted mt-0.5 max-w-xl">
            Le holdings dei tuoi ETF consolidate, con il peso effettivo di ogni società.
          </p>
        </header>

        <StatsBar
          etfCount={etfNames.length}
          uniqueHoldings={processedData.total_holdings?.length || 0}
        />
      </div>

      <div className="tabs-container">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Tabs
            testId="results-tabs"
            ariaLabel="Viste del portafoglio"
            variant="pill"
            value={activeTab}
            onChange={setActiveTab}
            items={portfolioViews}
          />

          {etfNames.length > 0 && (
            <div className="flex items-center gap-2" data-testid="results-etf-picker">
              <label
                htmlFor="results-etf-select"
                className="text-label uppercase tracking-wide text-content-muted"
              >
                Singolo ETF
              </label>
              {/* Follow-up: a pill per ETF did not scale. The views are the same,
                  they simply stay deliberately not URL-addressable. */}
              <FilterSelect
                id="results-etf-select"
                testid="results-etf-select"
                size="field"
                width="max-w-[240px]"
                value={isEtfView ? activeTab : ""}
                onChange={(value) => setActiveTab(value || "total-holdings")}
                options={etfNames}
                placeholder="Scegli un ETF…"
                aria-label="Mostra le holdings di un singolo ETF"
              />
            </div>
          )}
        </div>

        {activeTab === "total-holdings" && (
          <TotalHoldingsTab data={processedData.total_holdings} etfNames={etfNames} />
        )}

        {activeTab === "allocation" && (
          <AllocationTab
            data={processedData.allocation}
            sessionId={sessionId}
            onRecalculated={handleRecalculated}
          />
        )}

        {activeTab === "breakdown" && (
          <BreakdownTab data={processedData.total_holdings} />
        )}

        {activeTab === "diversification" && (
          <DiversificationTab
            sessionId={sessionId}
            portfolioIsins={(processedData.allocation || [])
              .map((row) => row.isin)
              .filter(Boolean)}
            allocationSignature={(processedData.allocation || [])
              .map((row) => `${row.isin || row.etf}:${row.allocation}`)
              .join("|")}
          />
        )}

        {etfNames.map((etf) => activeTab === etf && (
          <EtfTab key={etf} etf={etf} holdings={processedData.etf_data[etf]} />
        ))}
      </div>
    </div>
  );
}

export default DataPreview;
